import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface PipeMetric {
  pipe_id: string;
  pipe_name: string;
  provider: string;
  model: string;
  total_requests: number;
  total_input_tokens: number;
  total_output_tokens: number;
  total_tokens: number;
  total_cost: number;
  avg_latency_ms: number;
  error_count: number;
  success_rate: number;
}

interface TimeSeriesPoint {
  timestamp: string;
  input_tokens: number;
  output_tokens: number;
  requests: number;
  cost: number;
}

interface PipeMetrics {
  summaries: PipeMetric[];
  time_series: TimeSeriesPoint[];
  total_cost: number;
  total_tokens: number;
  total_requests: number;
}

interface PipeMetricsDashboardProps {
  selectedPipeId?: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

const PipeMetricsDashboard: React.FC<PipeMetricsDashboardProps> = ({ selectedPipeId }) => {
  const [metrics, setMetrics] = useState<PipeMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d' | '30d'>('24h');

  useEffect(() => {
    loadMetrics();
    const interval = setInterval(loadMetrics, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [selectedPipeId, timeRange]);

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({ time_range: timeRange });
      if (selectedPipeId) {
        params.append('pipe_id', selectedPipeId);
      }
      
      const response = await fetch(`/api/v1/pipes-metrics?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch metrics: ${response.status} ${response.statusText}`);
      }
      
      const data: PipeMetrics = await response.json();
      setMetrics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('Failed to load pipe metrics:', errorMessage);
      setError(errorMessage);
      setMetrics(null);
    } finally {
      setLoading(false);
    }
  };

  const pipeMetrics = metrics?.summaries || [];
  const timeSeriesData = metrics?.time_series || [];
  
  const totalMetrics = pipeMetrics.reduce((acc, m) => ({
    requests: acc.requests + m.total_requests,
    inputTokens: acc.inputTokens + m.total_input_tokens,
    outputTokens: acc.outputTokens + m.total_output_tokens,
    totalTokens: acc.totalTokens + m.total_tokens,
    cost: acc.cost + m.total_cost,
    errors: acc.errors + m.error_count,
  }), {
    requests: 0,
    inputTokens: 0,
    outputTokens: 0,
    totalTokens: 0,
    cost: 0,
    errors: 0,
  });

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(2)}K`;
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return `$${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-triangle mr-2"></i>
          <strong>Error loading metrics:</strong> {error}
        </div>
        <button className="btn btn-primary" onClick={loadMetrics}>
          <i className="fas fa-sync-alt mr-2"></i>
          Retry
        </button>
      </div>
    );
  }

  if (!metrics || metrics.summaries.length === 0) {
    return (
      <div className="container-fluid">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 mb-0 text-gray-800">
            <i className="fas fa-chart-line mr-2"></i>
            {selectedPipeId ? 'Pipe Metrics' : 'All Pipes Metrics'}
          </h1>
          <div className="btn-group" role="group">
            {(['1h', '24h', '7d', '30d'] as const).map((range) => (
              <button
                key={range}
                type="button"
                className={`btn btn-sm ${timeRange === range ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setTimeRange(range)}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        <div className="alert alert-info" role="alert">
          <i className="fas fa-info-circle mr-2"></i>
          No metrics data available for the selected time range. Execute some pipes to see metrics.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header */}
      <div className="d-sm-flex align-items-center justify-content-between mb-4">
        <h1 className="h3 mb-0 text-gray-800">
          <i className="fas fa-chart-line mr-2"></i>
          {selectedPipeId ? 'Pipe Metrics' : 'All Pipes Metrics'}
        </h1>
        <div className="btn-group" role="group">
          {(['1h', '24h', '7d', '30d'] as const).map((range) => (
            <button
              key={range}
              type="button"
              className={`btn btn-sm ${timeRange === range ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setTimeRange(range)}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-primary shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Requests
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {formatNumber(totalMetrics.requests)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-paper-plane fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-success shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Total Tokens
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {formatNumber(totalMetrics.totalTokens)}
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {formatNumber(totalMetrics.inputTokens)} in / {formatNumber(totalMetrics.outputTokens)} out
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-coins fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-info shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Total Cost
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {formatCurrency(totalMetrics.cost)}
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-dollar-sign fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-md-6 mb-4">
          <div className="card border-left-warning shadow h-100 py-2">
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    Error Rate
                  </div>
                  <div className="h5 mb-0 font-weight-bold text-gray-800">
                    {totalMetrics.requests > 0 
                      ? ((totalMetrics.errors / totalMetrics.requests) * 100).toFixed(2)
                      : '0.00'
                    }%
                  </div>
                  <div className="text-xs text-muted mt-1">
                    {totalMetrics.errors} errors
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-exclamation-triangle fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Series Charts */}
      <div className="row mb-4">
        <div className="col-xl-8">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Token Usage Over Time</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="input_tokens" 
                    stroke="#8884d8" 
                    name="Input Tokens"
                    strokeWidth={2}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="output_tokens" 
                    stroke="#82ca9d" 
                    name="Output Tokens"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div className="col-xl-4">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Token Distribution by Pipe</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pipeMetrics as any}
                    dataKey="total_tokens"
                    nameKey="pipe_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(entry: any) => `${entry.pipe_name}: ${formatNumber(entry.total_tokens)}`}
                  >
                    {pipeMetrics.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Cost Over Time */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">Cost Over Time</h6>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={timeSeriesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="timestamp" />
                  <YAxis />
                  <Tooltip formatter={(value: any) => formatCurrency(value as number)} />
                  <Legend />
                  <Bar dataKey="cost" fill="#ffc107" name="Cost (USD)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Pipes Table */}
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">Pipe Performance</h6>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-bordered table-hover">
              <thead>
                <tr>
                  <th>Pipe</th>
                  <th>Provider</th>
                  <th>Model</th>
                  <th>Requests</th>
                  <th>Input Tokens</th>
                  <th>Output Tokens</th>
                  <th>Total Tokens</th>
                  <th>Avg Latency</th>
                  <th>Cost</th>
                  <th>Success Rate</th>
                </tr>
              </thead>
              <tbody>
                {pipeMetrics.map((metric) => (
                  <tr key={metric.pipe_id}>
                    <td>
                      <strong>{metric.pipe_name}</strong>
                      <br />
                      <small className="text-muted">{metric.pipe_id}</small>
                    </td>
                    <td>
                      <span className="badge badge-info">{metric.provider}</span>
                    </td>
                    <td>
                      <code>{metric.model}</code>
                    </td>
                    <td>{formatNumber(metric.total_requests)}</td>
                    <td>{formatNumber(metric.total_input_tokens)}</td>
                    <td>{formatNumber(metric.total_output_tokens)}</td>
                    <td><strong>{formatNumber(metric.total_tokens)}</strong></td>
                    <td>{metric.avg_latency_ms}ms</td>
                    <td><strong>{formatCurrency(metric.total_cost)}</strong></td>
                    <td>
                      <span className={`badge ${metric.success_rate >= 99 ? 'badge-success' : metric.success_rate >= 95 ? 'badge-warning' : 'badge-danger'}`}>
                        {metric.success_rate.toFixed(2)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PipeMetricsDashboard;
